import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Footer } from './Footer';

const telematicsFaqs = [
  {
    question: "How much does fleet telematics cost in New Zealand?",
    answer: "Yuccan Tech fleet telematics starts at NZ$4.50 per asset per month on the Platform + Per-Asset plan (plus a $115/month platform fee), or NZ$11.99 per asset per month bundled with no platform fee — ideal for smaller fleets. Volume discounts apply for fleets over 100 assets. Contact us for a tailored quote."
  },
  {
    question: "What does real-time vehicle tracking include?",
    answer: "All plans include live GPS location, movement and geo-fence alerts, and recovery mode. Standard adds maintenance scheduling, compliance reports, and trip history. Advanced Pro adds fuel insights, AI-powered analytics, and API integrations for connecting your fleet data to other business systems."
  },
  {
    question: "Can I track non-vehicle assets — equipment and trailers?",
    answer: "Yes. Yuccan Tech's telematics platform monitors any mobile asset including vehicles, heavy equipment, trailers, and portable machinery. Each asset has its own reporting, alert history, and compliance record regardless of asset type."
  },
  {
    question: "Is the fleet management dashboard available on mobile?",
    answer: "Yes. A dedicated mobile driver app and responsive web dashboard are included in all plans. Drivers get trip logging and navigation; fleet managers get live dashboards, reports, and alert notifications from any device."
  }
];

const telematicsFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": telematicsFaqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
  }))
};

const telematicsBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://yuccan.tech/" },
    { "@type": "ListItem", "position": 2, "name": "Fleet Telematics", "item": "https://yuccan.tech/telematics" }
  ]
};

const TelematicsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    fleetSize: '',
    message: ''
  });

  const [transactionalConsent, setTransactionalConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [showPhoneHint, setShowPhoneHint] = useState(false);

  // Login/Session management state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  
  // Session configuration
  const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
  const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before expiry

  // Load reCAPTCHA script on component mount
  useEffect(() => {
    // Skip reCAPTCHA injection during react-snap / headless pre-rendering
    if (navigator.webdriver || window.location.hostname === 'localhost') return;
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF";
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      window.grecaptcha.ready(() => {
        console.log('reCAPTCHA v3 loaded successfully');
      });
    };
    
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
    };
    
    document.body.appendChild(script);

    return () => {
      // Clean up script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const filtered = value.replace(/[^+\d]/g, '');
      const sanitized = filtered.charAt(0) === '+'
        ? '+' + filtered.slice(1).replace(/\+/g, '')
        : filtered.replace(/\+/g, '');
      if (sanitized.length > 0 && sanitized.charAt(0) !== '+') {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
        return;
      }
      if (sanitized !== value) {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
      }
      setFormData(prev => ({ ...prev, phone: sanitized }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Execute reCAPTCHA v3 if available
      let recaptchaToken = null;
      if (window.grecaptcha) {
        recaptchaToken = await new Promise((resolve) => {
          window.grecaptcha.ready(async () => {
            try {
              const token = await window.grecaptcha.execute('6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF', {action: 'submit'});
              resolve(token);
            } catch (error) {
              resolve(null);
            }
          });
        });
      }

      // Prepare form data for API (match main contact form structure)
      const apiFormData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone.trim(),
        transactionalConsent: formData.phone.trim() ? transactionalConsent : false,
        marketingConsent: formData.phone.trim() ? marketingConsent : false,
        message: `Company: ${formData.company}\nFleet Size: ${formData.fleetSize}\n\nMessage: ${formData.message}`,
        formStartTime: Date.now() - 10000, // Anti-spam timing
        honeypot: '', // Anti-spam field
        recaptchaToken // Add reCAPTCHA token
      };

      // Make API call to backend
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiFormData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage('Thank you! We\'ll contact you within 24 hours to discuss your telematics needs.');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          fleetSize: '',
          message: ''
        });
        setTransactionalConsent(false);
        setMarketingConsent(false);
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly on +64 9 886 4638.');
    }
    
    setIsSubmitting(false);
  };

  // Session management functions
  const handleLogout = useCallback((showAlert = true) => {
    const sessionToken = sessionStorage.getItem('telematics_session');
    
    // Clear local storage and state immediately - stops the timer
    localStorage.removeItem('telematics_login_time');
    sessionStorage.removeItem('telematics_session');
    setIsLoggedIn(false);
    setSessionTimeLeft(0);
    setShowWarning(false);
    
    // Call backend logout if we have a session token
    if (sessionToken) {
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/telematics/logout/${sessionToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(response => response.json())
      .then(data => {
        console.log('Backend logout:', data.message);
      })
      .catch(error => {
        console.error('Backend logout error:', error);
      });
    }
    
    // Only show alert and open external logout if this is a manual logout
    if (showAlert) {
      // Open external logout page to complete logout from external system
      console.log('Opening external logout page...');
      window.open('https://yuccantech.telematics.guru/Account', '_blank');
      
      // Show user confirmation
      alert('Logged out from telematics dashboard. Please complete logout in the external system tab that just opened.');
    }
  }, []);

  const checkLoginStatus = useCallback(() => {
    const sessionToken = sessionStorage.getItem('telematics_session');
    
    // If no session token, definitely not logged in (silent cleanup)
    if (!sessionToken) {
      handleLogout(false); // Don't show alert during initialization
      return false;
    }
    
    // Validate session with backend
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/telematics/session/${sessionToken}`)
      .then(response => response.json())
      .then(data => {
        if (data.success && data.session) {
          // Session is valid on backend
          console.log('Session validated with backend:', data.session);
          localStorage.setItem('telematics_login_time', data.session.loginTime.toString());
          setIsLoggedIn(true);
          setSessionTimeLeft(data.session.timeRemaining);
        } else {
          // Session invalid or expired on backend
          console.log('Backend session validation failed:', data.message);
          handleLogout(false);
        }
      })
      .catch(error => {
        console.error('Backend session validation error:', error);
        // If backend is unavailable, fall back to local validation
        const loginTime = localStorage.getItem('telematics_login_time');
        if (loginTime) {
          const loginTimestamp = parseInt(loginTime);
          const currentTime = Date.now();
          const timeElapsed = currentTime - loginTimestamp;
          
          if (timeElapsed < SESSION_DURATION) {
            console.log('Using local session validation (backend unavailable)');
            setIsLoggedIn(true);
            setSessionTimeLeft(SESSION_DURATION - timeElapsed);
          } else {
            handleLogout(false);
          }
        } else {
          handleLogout(false);
        }
      });
  }, [handleLogout, SESSION_DURATION]);

  const handleLogin = () => {
    // Open existing login page in new tab
    const loginWindow = window.open('https://yuccantech.telematics.guru/', '_blank');
    
    console.log('Login window opened, waiting for webhook notification...');
    
    // Start polling for successful login via webhook
    const pollForLogin = () => {
      // Check if there are any new sessions from webhook
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/telematics/sessions/latest`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.success && data.session) {
          // Login successful via webhook
          console.log('Login successful via webhook:', data.session);
          localStorage.setItem('telematics_login_time', data.session.loginTime.toString());
          sessionStorage.setItem('telematics_session', data.session.token);
          setIsLoggedIn(true);
          setSessionTimeLeft(data.session.timeRemaining || SESSION_DURATION);
          setShowWarning(false);
          
          // Clean up polling
          clearInterval(pollInterval);
          clearInterval(checkWindowClosed);
          
          // Close login window if still open
          if (loginWindow && !loginWindow.closed) {
            loginWindow.close();
          }
        }
      })
      .catch(error => {
        console.error('Error checking for webhook login:', error);
      });
    };
    
    // Poll every 2 seconds for successful login
    const pollInterval = setInterval(pollForLogin, 2000);
    
    // Stop polling after 5 minutes or if window is closed
    const checkWindowClosed = setInterval(() => {
      if (loginWindow.closed) {
        clearInterval(pollInterval);
        clearInterval(checkWindowClosed);
        console.log('Login window closed, stopped polling for webhook');
      }
    }, 1000);
    
    // Cleanup after 5 minutes maximum
    setTimeout(() => {
      clearInterval(pollInterval);
      clearInterval(checkWindowClosed);
    }, 5 * 60 * 1000);
  };

  const formatTimeLeft = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // External session validation
  const checkExternalSession = useCallback(() => {
    if (!isLoggedIn) return;
    
    // Try to access external system to check if still logged in
    // We'll use a hidden iframe to test access without redirecting user
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = 'https://yuccantech.telematics.guru/';
    
    iframe.onload = () => {
      // If the iframe loads successfully, user might still be logged in
      // If it redirects to login, we can't easily detect that from iframe
      // This is a limitation of cross-origin security
      document.body.removeChild(iframe);
    };
    
    iframe.onerror = () => {
      // If there's an error, assume logout
      console.log('External session validation failed - assuming logout');
      handleLogout();
      document.body.removeChild(iframe);
    };
    
    document.body.appendChild(iframe);
    
    // Remove iframe after timeout
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 5000);
  }, [isLoggedIn, handleLogout]);

  // Session monitoring effect
  useEffect(() => {
    // Check login status on component mount
    checkLoginStatus();

    // Set up interval to check session and update timer
    const interval = setInterval(() => {
      if (isLoggedIn) {
        const loginTime = localStorage.getItem('telematics_login_time');
        if (loginTime) {
          const loginTimestamp = parseInt(loginTime);
          const currentTime = Date.now();
          const timeElapsed = currentTime - loginTimestamp;
          const timeLeft = SESSION_DURATION - timeElapsed;
          
          if (timeLeft <= 0) {
            // Session expired
            handleLogout(false); // Don't show external logout for automatic expiry
            alert('Your session has expired. Please log in again.');
          } else {
            setSessionTimeLeft(timeLeft);
            
            // Show warning when 5 minutes or less remaining
            if (timeLeft <= WARNING_TIME && !showWarning) {
              setShowWarning(true);
            }
          }
        }
      }
    }, 1000); // Update every second

    // Check external session every 2 minutes
    const externalCheckInterval = setInterval(() => {
      checkExternalSession();
    }, 2 * 60 * 1000); // Check every 2 minutes

    // Clean up intervals on unmount
    return () => {
      clearInterval(interval);
      clearInterval(externalCheckInterval);
    };
  }, [isLoggedIn, showWarning, SESSION_DURATION, WARNING_TIME, handleLogout, checkLoginStatus, checkExternalSession]);

  // Listen for storage changes (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'telematics_login_time') {
        if (e.newValue) {
          checkLoginStatus();
        } else {
          setIsLoggedIn(false);
          setSessionTimeLeft(0);
          setShowWarning(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkLoginStatus]);

  return (
    <div>
      <Helmet>
        <title>Fleet Telematics & Vehicle Tracking NZ | Yuccan Tech</title>
        <meta name="description" content="Real-time GPS vehicle tracking, driver behaviour monitoring, and fleet management from NZ$4.50/asset/month. Comprehensive fleet telematics for NZ businesses." />
        <link rel="canonical" href="https://yuccan.tech/telematics" />
        <meta property="og:title" content="Fleet Telematics & Vehicle Tracking NZ | Yuccan Tech" />
        <meta property="og:description" content="Real-time GPS tracking, driver behaviour monitoring, and fleet compliance from NZ$4.50/asset/month. Yuccan Tech fleet telematics." />
        <meta property="og:url" content="https://yuccan.tech/telematics" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(telematicsFaqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(telematicsBreadcrumb)}</script>
      </Helmet>
    <div className="telematics-page">
      {/* Logo Header */}
      <div className="telematics-header">
        <div className="container">
          <div className="header-content">
            <div className="logo-container">
              <a href="/">
                <img 
                  src="/img/Banner_16x_TR_Trim&ColorEdit.png" 
                  alt="Yuccan Technologies Logo" 
                  className="telematics-logo"
                />
              </a>
            </div>
            <div className="login-container">
              {isLoggedIn ? (
                <div className="session-display">
                  <span className="session-timer">
                    {showWarning && '⚠️ '} Session: {formatTimeLeft(sessionTimeLeft)}
                  </span>
                </div>
              ) : (
                <button 
                  onClick={handleLogin}
                  className="login-logout-btn login-btn"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Session Warning Notification */}
      {showWarning && isLoggedIn && (
        <div className="session-warning">
          <div className="warning-content">
            <span className="warning-icon">⚠️</span>
            <span className="warning-text">
              Your session will expire in {formatTimeLeft(sessionTimeLeft)}. 
              Your work will be saved automatically.
            </span>
            <button 
              onClick={() => setShowWarning(false)}
              className="warning-close"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="telematics-hero">
        <video 
          autoPlay
          muted
          loop
          playsInline
          onPlay={() => {
            // Release wake lock to allow screen sleep
            if ('wakeLock' in navigator) {
              navigator.wakeLock.request('screen').then(wakeLock => {
                wakeLock.release();
              }).catch(() => {});
            }
          }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            top: 0,
            left: 0,
            zIndex: -1
          }}
        >
          <source src="/vid/Telematics.mp4" type="video/mp4" />
        </video>
        
        <div className="hero-overlay">
          <div className="container">
            <div className="row">
              <div className="col-md-6">
                <div className="hero-content-overlay">
                  <h1 className="hero-title">
                    Complete Asset Visibility.<br />
                    <span className="text-primary">Beyond Your Premises.</span>
                  </h1>
                  <p className="hero-subtitle">
                    Extend your existing security and monitoring capabilities to your mobile assets. 
                    Just as you have video surveillance, access control, and monitoring for your premises, 
                    our telematics solutions provide the same comprehensive visibility for vehicles, 
                    equipment, and other mobile assets; ensuring operational efficiency, security, and full compliance.
                  </p>
                  <div className="hero-stats">
                    <div className="stat-item">
                      <div className="stat-number">100%</div>
                      <div className="stat-label">Asset Visibility</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">24/7</div>
                      <div className="stat-label">Real-time Monitoring</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">Full</div>
                      <div className="stat-label">Compliance Coverage</div>
                    </div>
                  </div>
                  <div className="hero-cta">
                    <a href="#pricing" className="btn btn-outline-primary btn-lg">View Pricing</a>
                    <a href="#contact" className="btn btn-primary btn-lg">Book Your Demo</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile-only Hero Content Section */}
      <section className="mobile-hero-content">
        <div className="container">
          <div className="row">
            <div className="col-xs-12">
              <div className="mobile-hero-content-container">
                <h1 className="mobile-hero-title">
                  Complete Asset Visibility.<br />
                  <span className="text-primary">Beyond Your Premises.</span>
                </h1>
                <p className="mobile-hero-subtitle">
                  Extend your existing security and monitoring capabilities to your mobile assets. 
                  Just as you have video surveillance, access control, and monitoring for your premises, 
                  our telematics solutions provide the same comprehensive visibility for vehicles, 
                  equipment, and other mobile assets; ensuring operational efficiency, security, and full compliance.
                </p>
                <div className="mobile-hero-stats">
                  <div className="mobile-stat-item">
                    <div className="mobile-stat-number">100%</div>
                    <div className="mobile-stat-label">Asset Visibility</div>
                  </div>
                  <div className="mobile-stat-item">
                    <div className="mobile-stat-number">24/7</div>
                    <div className="mobile-stat-label">Real-time Monitoring</div>
                  </div>
                  <div className="mobile-stat-item">
                    <div className="mobile-stat-number">Full</div>
                    <div className="mobile-stat-label">Compliance Coverage</div>
                  </div>
                </div>
                <div className="mobile-hero-cta">
                  <a href="#pricing" className="btn btn-outline-primary btn-lg">View Pricing</a>
                  <a href="#contact" className="btn btn-primary btn-lg">Book Your Demo</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section id="benefits" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Complete Mobile Asset Management</h2>
            <p>Mirror your in-premise security, control, and data management systems for all your mobile assets; achieve the same operational efficiency, security, and compliance standards everywhere</p>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-shield"></i>
                <div className="service-desc">
                  <h3>Security & Access Control</h3>
                  <p>Just like with your in-premise security system; monitor if, when, where, and by whom, your mobile assets are accessed. Get real-time alerts, driver identification, unauthorized usage detection, and evidence for insurance and legal purposes.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-dollar"></i>
                <div className="service-desc">
                  <h3>Operational Efficiency</h3>
                  <p>Vehicle route tracking, dynamic route planning, automated maintenance scheduling, driver behaviour analysis, tolls and parking compliance, and much more; all managed as seamlessly as your in-house facility management systems.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-clock"></i>
                <div className="service-desc">
                  <h3>Complete Compliance</h3>
                  <p>Automated compliance reporting for road user charges, insurance claims, legal requirements, safety standards, and regulatory obligations. Get full documentation and audit trails, just like you do for your in-premise monitoring systems.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Core Features</h2>
            <p>Everything you need to manage your fleet effectively in one integrated platform</p>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-map-marker"></i>
                <div className="service-desc">
                  <h3>Real-Time GPS Tracking</h3>
                  <p>Live location tracking with 10-second update intervals. Geofencing alerts, route replay, and comprehensive location history for complete fleet visibility.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-tachometer"></i>
                <div className="service-desc">
                  <h3>Driver Behavior Monitoring</h3>
                  <p>Track speeding, harsh braking, rapid acceleration, and cornering. Automated driver scorecards and coaching tools to improve safety performance.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-tint"></i>
                <div className="service-desc">
                  <h3>Fuel Management</h3>
                  <p>Monitor fuel consumption, detect unauthorized usage, and optimize routes for maximum fuel efficiency. Integration with fuel card systems available.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-wrench"></i>
                <div className="service-desc">
                  <h3>Predictive Maintenance</h3>
                  <p>Automated maintenance scheduling based on mileage, engine hours, or time intervals. Reduce breakdowns and extend vehicle life.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-mobile"></i>
                <div className="service-desc">
                  <h3>Mobile Driver App</h3>
                  <p>Native iOS and Android apps for drivers with trip logging, digital forms, communication tools, and performance feedback.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-bar-chart"></i>
                <div className="service-desc">
                  <h3>Advanced Reporting</h3>
                  <p>Comprehensive dashboards and customizable reports. Export data for accounting, compliance, or further analysis.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications Section */}
      <section id="industries" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Trusted Across Industries</h2>
            <p>Our telematics solutions deliver results for businesses of all sizes and types</p>
          </div>
          <div className="row">
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-logistics" style={{backgroundImage: 'url(/img/telematics/industry-logistics.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Logistics & Delivery</h3>
                  <p>Route optimization, proof of delivery, and customer communication for improved service levels.</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-construction" style={{backgroundImage: 'url(/img/telematics/industry-construction.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Construction</h3>
                  <p>Equipment tracking, site security, maintenance scheduling, and compliance reporting for heavy machinery.</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-field-services" style={{backgroundImage: 'url(/img/telematics/industry-field-services.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Field Services</h3>
                  <p>Technician dispatch, job scheduling, customer updates, and service history tracking.</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-transportation" style={{backgroundImage: 'url(/img/telematics/industry-transportation.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Transportation</h3>
                  <p>Passenger safety, route compliance, driver performance, and operational efficiency for transport operators.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Showcase */}
      <section id="technology" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Cutting-Edge Technology Stack</h2>
            <p>Enterprise-grade hardware and software designed for turnkey global use</p>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="technology-card">
                <div className="technology-photo technology-dashboard" style={{backgroundImage: 'url(/img/telematics/technology-dashboard.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Intuitive Web Interface</h3>
                  <p>Clean, responsive interface providing instant access to critical fleet information. Customizable parameters and real-time data access.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="technology-card">
                <div className="technology-photo technology-mobile" style={{backgroundImage: 'url(/img/telematics/technology-mobile.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Mobile Applications</h3>
                  <p>Native iOS and Android apps for drivers and managers. Offline capability and seamless synchronization when connectivity returns.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="technology-card">
                <div className="technology-photo technology-hardware" style={{backgroundImage: 'url(/img/telematics/technology-hardware.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Robust Hardware</h3>
                  <p>Professional-grade GPS devices with cellular connectivity, crash detection, and tamper alerts. Appropriate IP ratings for harsh conditions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="text-center" style={{backgroundColor: '#e9ecef', padding: '30px 0'}}>
        <div className="container">
          <div className="section-title">
            <h2>Transparent Pricing</h2>
            <p>Simple, scalable pricing tiers with no hidden fees*. Choose the billing structure that fits your fleet size and growth strategy.</p>
			<p style={{fontSize: '14px', color: '#333', fontStyle: 'italic', marginTop: '10px'}}>*Please note that the quoted prices are in New Zealand dollars, inclusive of GST. Prices in other currencies will differ slightly.</p>
          </div>

          {/* Pricing Toggle */}
          <div className="pricing-toggle" style={{marginBottom: '30px'}}>
            <div style={{
              display: 'inline-flex',
              backgroundColor: 'white',
              borderRadius: '50px',
              padding: '4px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              marginBottom: '20px'
            }}>
              <button 
                className="pricing-tab-btn"
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '50px',
                  backgroundColor: '#f8825a',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginRight: '2px'
                }}
                onClick={() => {
                  // Switch to Option B
                  document.getElementById('optionA').style.display = 'none';
                  document.getElementById('optionB').style.display = 'block';
                  document.querySelectorAll('.pricing-tab-btn').forEach((btn, i) => {
                    btn.style.backgroundColor = i === 0 ? '#f8825a' : 'transparent';
                    btn.style.color = i === 0 ? 'white' : '#333';
                  });
                }}
              >
                Bundled (No Platform Fee)
              </button>
              <button 
                className="pricing-tab-btn"
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '50px',
                  backgroundColor: 'transparent',
                  color: '#333',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  // Switch to Option A
                  document.getElementById('optionA').style.display = 'block';
                  document.getElementById('optionB').style.display = 'none';
                  document.querySelectorAll('.pricing-tab-btn').forEach((btn, i) => {
                    btn.style.backgroundColor = i === 1 ? '#f8825a' : 'transparent';
                    btn.style.color = i === 1 ? 'white' : '#333';
                  });
                }}
              >
                Platform + Per-Asset
              </button>
            </div>
          </div>

          {/* Option A - Platform + Per-Asset */}
          <div id="optionA" className="pricing-option" style={{display: 'none'}}>
            <div className="row">
              <div className="col-md-10 col-md-offset-1">
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  padding: '40px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  marginBottom: '30px'
                }}>
                  <p style={{fontSize: '18px', marginBottom: '10px'}}>
                    <strong style={{color: '#f8825a'}}>Platform fee of $115/month</strong> + Per-asset pricing tiers
                  </p>
                  <p style={{color: '#333', marginBottom: '30px'}}>
                    Best for businesses with 20+ assets who need personalized branding, itemized billing control and advanced cost management.
                  </p>
                  
                  <div className="row" style={{marginTop: '30px'}}>
                    <div className="col-md-4">
                      <div style={{padding: '5px', border: '2px solid #e9ecef', borderRadius: '10px', marginBottom: '20px'}}>
                        <h3 style={{color: '#f8825a'}}>Basic Plus</h3>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#333'}}>$4.50</div>
                        <div style={{color: '#333', fontSize: '14px', marginBottom: '15px'}}>per asset, per month*</div>
                        <div style={{fontSize: '14px', color: '#333', lineHeight: '1.4'}}>Live GPS, alerts, recovery mode, & history</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{padding: '5px', border: '2px solid #f8825a', borderRadius: '10px', marginBottom: '20px', position: 'relative', transform: 'scale(1.05)'}}>
                        <div style={{
                          position: 'absolute', 
                          top: '-10px', 
                          right: '20px', 
                          backgroundColor: '#f8825a', 
                          color: 'white', 
                          padding: '4px 12px', 
                          borderRadius: '20px', 
                          fontSize: '12px', 
                          fontWeight: 'bold'
                        }}>MOST POPULAR</div>
                        <h3 style={{color: '#f8825a'}}>Standard</h3>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#333'}}>$7.00</div>
                        <div style={{color: '#333', fontSize: '14px', marginBottom: '15px'}}>per asset, per month*</div>
                        <div style={{fontSize: '14px', color: '#333', lineHeight: '1.4'}}><strong>Basic Plus</strong> + reports, maintenance, & compliance</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{padding: '5px', border: '2px solid #e9ecef', borderRadius: '10px', marginBottom: '20px'}}>
                        <h3 style={{color: '#f8825a'}}>Advanced Pro</h3>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#333'}}>$9.00</div>
                        <div style={{color: '#333', fontSize: '14px', marginBottom: '15px'}}>per asset, per month*</div>
                        <div style={{fontSize: '14px', color: '#333', lineHeight: '1.4'}}><strong>Standard</strong> + fuel insights, API & AI integrations, & advanced analytics</div>
                      </div>
                    </div>
                  </div>

                  <p style={{fontSize: '14px', color: '#333', fontStyle: 'italic', marginTop: '20px'}}>
                    *Volume discounts available for large fleets exceeding 100 assets.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Option B - Bundled */}
          <div id="optionB" className="pricing-option">
            <div className="row">
              <div className="col-md-10 col-md-offset-1">
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  padding: '40px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  marginBottom: '30px'
                }}>
                  <p style={{fontSize: '18px', marginBottom: '10px'}}>
                    <strong style={{color: '#f8825a'}}>No platform fee.</strong> Simple per-asset pricing tiers with volume discounts.
                  </p>
                  <p style={{color: '#333', marginBottom: '30px'}}>
                    Perfect for small-to-mid fleets that want "one price per asset, no surprises". Annual prepay discounts included.
                  </p>
                  
                  <div className="row" style={{marginTop: '30px'}}>
                    <div className="col-md-4">
                      <div style={{padding: '5px', border: '2px solid #e9ecef', borderRadius: '10px', marginBottom: '20px'}}>
                        <h3 style={{color: '#f8825a'}}>Basic</h3>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#333'}}>$11.99</div>
                        <div style={{color: '#333', fontSize: '14px', marginBottom: '10px'}}>per asset, per month*</div>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#28a745'}}>$126.00</div>
                        <div style={{color: '#28a745', fontSize: '14px', marginBottom: '15px'}}>annual prepay ($10.50/month; 12% off)</div>
                        <div style={{fontSize: '14px', color: '#333', lineHeight: '1.4'}}>Live GPS, alerts, recovery mode, no history</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{padding: '5px', border: '2px solid #f8825a', borderRadius: '10px', marginBottom: '20px', position: 'relative', transform: 'scale(1.05)'}}>
                        <div style={{
                          position: 'absolute', 
                          top: '-10px', 
                          right: '20px', 
                          backgroundColor: '#f8825a', 
                          color: 'white', 
                          padding: '4px 12px', 
                          borderRadius: '20px', 
                          fontSize: '12px', 
                          fontWeight: 'bold'
                        }}>MOST POPULAR</div>
                        <h3 style={{color: '#f8825a'}}>Standard</h3>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#333'}}>$17.99</div>
                        <div style={{color: '#333', fontSize: '14px', marginBottom: '10px'}}>per asset, per month*</div>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#28a745'}}>$192.00</div>
                        <div style={{color: '#28a745', fontSize: '14px', marginBottom: '15px'}}>annual prepay ($16/month; 11% off)</div>
                        <div style={{fontSize: '14px', color: '#333', lineHeight: '1.4'}}><strong>Basic</strong> + history, reports, maintenance, & compliance</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{padding: '5px', border: '2px solid #e9ecef', borderRadius: '10px', marginBottom: '20px'}}>
                        <h3 style={{color: '#f8825a'}}>Advanced</h3>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#333'}}>$21.99</div>
                        <div style={{color: '#333', fontSize: '14px', marginBottom: '10px'}}>per asset, per month*</div>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#28a745'}}>$234.00</div>
                        <div style={{color: '#28a745', fontSize: '14px', marginBottom: '15px'}}>annual prepay ($19.50/month; 11% off)</div>
                        <div style={{fontSize: '14px', color: '#333', lineHeight: '1.4'}}><strong>Standard</strong> + fuel insights, integrations, & analytics</div>
                      </div>
                    </div>
                  </div>

                  <p style={{fontSize: '14px', color: '#333', fontStyle: 'italic', marginTop: '20px'}}>
                    *Annual prepay discount price also applies monthly to the 11th asset onwards, applied to the highest tier.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Optional Add-ons Section */}
          <div style={{marginTop: '30px'}}>
            <div className="row">
              <div className="col-md-10 col-md-offset-1">
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  padding: '40px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  marginBottom: '30px'
                }}>
                  <div style={{color: '#f8825a', marginBottom: '10px', textAlign: 'center', fontSize: '18px', fontWeight: 'bold'}}>More Options</div>				  
				  <p style={{color: '#333', marginBottom: '30px'}}>
                    Available with both pricing options to enhance your fleet management capabilities.
                  </p>
                  
                  <div className="row" style={{marginTop: '30px'}}>
                    <div className="col-md-4">
                      <div style={{padding: '25px', backgroundColor: '#f8f9fa', borderRadius: '10px', marginBottom: '20px', textAlign: 'center'}}>
                        <h4 style={{color: '#333'}}>RFID Tags</h4>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#333'}}>from $3.50</div>
                        <div style={{color: '#333', fontSize: '14px', marginBottom: '15px'}}>per tag, per month</div>
                        <div style={{fontSize: '14px', color: '#333', lineHeight: '1.4'}}>Low-cost driver ID, tools & equipment tagging</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{padding: '25px', backgroundColor: '#f8f9fa', borderRadius: '10px', marginBottom: '20px', textAlign: 'center'}}>
                        <h4 style={{color: '#333'}}>Video Options*</h4>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#333'}}>$10 / $5</div>
                        <div style={{color: '#333', fontSize: '14px', marginBottom: '15px'}}>standalone / add-on, per month</div>
                        <div style={{fontSize: '14px', color: '#333', lineHeight: '1.4'}}>Dashcam events, video storage & playback, image geo-tagging</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{padding: '25px', backgroundColor: '#f8f9fa', borderRadius: '10px', marginBottom: '20px', textAlign: 'center'}}>
                        <h4 style={{color: '#333'}}>Satellite Fallback**</h4>
                        <div style={{fontSize: '28px', fontWeight: 'bold', color: '#333'}}>$9.00</div>
                        <div style={{color: '#333', fontSize: '14px', marginBottom: '15px'}}>per device, per month</div>
                        <div style={{fontSize: '14px', color: '#333', lineHeight: '1.4'}}>Improved accuracy, extended coverage outside cellular networks</div>
                      </div>
                    </div>
                  </div>
				  <p style={{fontSize: '14px', color: '#333', fontStyle: 'italic', marginTop: '3px', marginBottom: '3px'}}>
					*Requires separate subscription for video platform; **Requires separate subscription for direct satellite GPS.</p>
				  <p style={{fontSize: '14px', color: '#333', fontStyle: 'italic'}}>
				    Please get in touch to find out more.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="text-center" style={{padding: '30px 0'}}>
        <div className="container">
          <div className="section-title">
            <h2>Frequently Asked Questions</h2>
            <p>The basics you need to know about our telematics solutions, pricing, and implementation.</p>
          </div>
          
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
              <div className="faq-container">
                
                {/* Pricing FAQs */}
                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>What would it cost for a small business with 10 trucks and 2 trailers?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    <strong>Option A (Bundled):</strong><br/>
                    (8 Standard assets × $17.99) + (2 volume discounted Standard assets × $16.00) + (2 Basic assets × $11.99) = $199.90/month<br/><br/>
					<strong>Option B (Platform + Per-Asset):</strong><br/>
                    Platform fee: $115/month + (10 Standard assets × $7.00) + (2 Basic Plus assets × $4.50) = $194/month<br/><br/>
                    <em>For this fleet size, Option B saves you approximately $6/month. However, annual prepayments will cost you (10 Standard assets × $16.00) + (2 Basic assets × $10.50) = $181/month, thereby netting you a saving of $13/month for Option A over Option B.</em>
                  </div>
                </div>

                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>What about an enterprise fleet with 50 trucks, 20 cameras, and 40 tags?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    <strong>Option A (Bundled, annual prepayment):</strong><br/>
					50 trucks (Standard Tier): 50 × $16.00 = $800.00<br/>
                    20 add-on cameras: 20 × $5.00 = $100<br/>
                    40 Bluetooth tags: 40 × $3.50 = $140<br/>
                    <strong>Total: $1,040/month</strong><br/><br/>
					<strong>Option B (Platform + Per-Asset):</strong><br/>
                    Platform fee: $115/month<br/>
                    50 trucks (Standard Tier): 50 × $7.00 = $350<br/>
                    20 add-on cameras: 20 × $5.00 = $100<br/>
                    40 Bluetooth tags: 40 × $3.50 = $140<br/>
                    <strong>Total: $590/month</strong><br/><br/>
                    <em>For enterprise fleets, Option B provides a huge savings of $450.00/month, even with annual prepayment for Option A!</em>
                  </div>
                </div>

                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>Can I switch between pricing options later?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    Yes, at renewal you can move from Bundled to Platform + Asset or vice versa. We'll help you analyze which option provides the best value as your fleet grows or changes.
                  </div>
                </div>

                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>Are there any hidden fees or long-term contracts?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    No hidden fees whatsoever. What you see is what you pay. Hardware costs are separate and disclosed upfront. We offer both month-to-month flexibility and annual contracts (with minimum 11% discount).
                  </div>
                </div>

                {/* Technical FAQs */}
                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>How difficult is the installation process?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    Our devices are designed for easy installation. Most vehicle units plug directly into the OBD-II port (no wiring required), while advanced installations with hardwiring typically take 30-45 minutes per vehicle. Our battery-powered trackers can be mounted on many types of surfaces. We provide full installation support and can arrange professional installation if needed.
                  </div>
                </div>

                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>Can I use my own tracker and/or peripherals?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    This depends on the specific hardware. This is usually okay, but certain hardware and/or firmware limitations may make it difficult to justify the engineering time required to get the device(s) working properly with our platform. Please contact us to discuss this option.
                  </div>
                </div>

                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>What's the difference between the two pricing options in terms of features?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    The mid (Standard) tier is identical for both options. The differences in the low (Basic vs Basic Plus) and high (Advanced vs Advanced Pro) tiers are because the paid platform option allows for dedicated data storage, which ultimately allows for straightforward historical data retrieval, API configuration, AI integration, and advanced analytics.
                  </div>
                </div>

                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>How often does the GPS tracking update?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    Our standard tracking can provide updates every 30 seconds when the vehicle is in motion, with real-time alerts for critical events. Faster updates are possible on the Advanced Tier. Stationary vehicles update every quarter hour to six hours depending, to conserve battery life and data usage. High-resolution tracking with 2-second updates is available for specialized requirements. Please get in touch to find out more.
                  </div>
                </div>

                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>What happens if a vehicle goes outside cellular coverage?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    Our devices store location data locally and automatically sync when cellular coverage returns. For critical operations in remote areas, we offer satellite fallback options using the Iridium network for truly global coverage at $9/month per device.
                  </div>
                </div>

                <div className="faq-item" style={{marginBottom: '25px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                  <div 
                    className="faq-question"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    onClick={(e) => {
                      const answer = e.currentTarget.nextElementSibling;
                      const icon = e.currentTarget.querySelector('.faq-icon');
                      if (answer.style.display === 'none' || !answer.style.display) {
                        answer.style.display = 'block';
                        icon.innerHTML = '−';
                      } else {
                        answer.style.display = 'none';
                        icon.innerHTML = '+';
                      }
                    }}
                  >
                    <span>Do you provide training and ongoing support?</span>
                    <span className="faq-icon" style={{fontSize: '20px', color: '#f8825a'}}>+</span>
                  </div>
                  <div className="faq-answer" style={{padding: '20px', display: 'none', color: '#333', textAlign: 'left'}}>
                    Absolutely. We provide comprehensive onboarding training, online tutorials, and ongoing technical support. Our support team is available during business hours (9am to 5pm weekdays), with support available for critical fleet operations. Training sessions can be conducted remotely or on-site, depending on your needs.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="text-center" style={{paddingTop: '60px', paddingBottom: '60px', backgroundColor: '#f8f9fa'}}>
        <div className="container">
          <div className="section-title">
            <h2>Fleet Telematics — Frequently Asked Questions</h2>
          </div>
          <div className="row">
            <div className="col-md-8 col-md-offset-2" style={{textAlign: 'left'}}>
              {telematicsFaqs.map((faq, i) => (
                <div key={i} style={{borderBottom: '1px solid #dee2e6', marginBottom: '0'}}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      width: '100%', padding: '18px 0', background: 'none', border: 'none',
                      textAlign: 'left', fontSize: '16px', fontWeight: '600', color: '#333', cursor: 'pointer'
                    }}
                  >
                    <span>{faq.question}</span>
                    <span style={{fontSize: '20px', color: '#f8825a', marginLeft: '16px', flexShrink: 0}}>
                      {openFaq === i ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === i && (
                    <div style={{paddingBottom: '18px', color: '#555', lineHeight: '1.7', fontSize: '15px'}}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact/Get Started Section */}
      <section id="contact" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Ready to Optimize Your Fleet?</h2>
            <p>Get started with a free no-obligation demo and fleet assessment; discover how much you could save with our telematics solution.</p>
          </div>
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
                <form onSubmit={handleSubmit} className="telematics-contact-form">
                  <div className="form-group">
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="Email Address *"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="text"
                          name="company"
                          className="form-control"
                          placeholder="Company Name"
                          value={formData.company}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group" style={{position: 'relative'}}>
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                        {showPhoneHint && (
                          <div style={{
                            position: 'absolute', top: '-38px', left: '0', right: '0',
                            backgroundColor: '#333', color: '#fff', fontSize: '12px',
                            padding: '6px 10px', borderRadius: '4px', textAlign: 'center',
                            zIndex: 10, whiteSpace: 'nowrap'
                          }}>
                            Only + followed by digits allowed
                            <div style={{
                              position: 'absolute', bottom: '-6px', left: '50%', marginLeft: '-6px',
                              width: 0, height: 0, borderLeft: '6px solid transparent',
                              borderRight: '6px solid transparent', borderTop: '6px solid #333'
                            }} />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <select
                          name="fleetSize"
                          className="form-control"
                          value={formData.fleetSize}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Fleet Size</option>
                          <option value="1-5">1-5 vehicles</option>
                          <option value="6-20">6-20 vehicles</option>
                          <option value="21-50">21-50 vehicles</option>
                          <option value="51-100">51-100 vehicles</option>
                          <option value="100+">100+ vehicles</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <textarea
                      name="message"
                      className="form-control"
                      rows="4"
                      placeholder="Tell us about your fleet challenges or goals..."
                      value={formData.message}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    ></textarea>
                  </div>

                  {formData.phone.trim() && (
                    <>
                      <div style={{clear: 'both', overflow: 'hidden', marginTop: '-5px', marginBottom: '10px'}}>
                        <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                          <input
                            type="checkbox"
                            checked={transactionalConsent}
                            onChange={(e) => setTransactionalConsent(e.target.checked)}
                            disabled={isSubmitting}
                            style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                          />
                          <span style={{textAlign: 'justify'}}>
                            By checking this box, I consent to receive non-marketing text messages from Yuccan Technologies at the phone number provided. Messages may include responses to inquiries, appointment or consultation confirmations, service updates, and follow-up communications relevant to your request. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                          </span>
                        </label>
                      </div>
                      <div style={{clear: 'both', overflow: 'hidden', marginBottom: '15px'}}>
                        <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                          <input
                            type="checkbox"
                            checked={marketingConsent}
                            onChange={(e) => setMarketingConsent(e.target.checked)}
                            disabled={isSubmitting}
                            style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                          />
                          <span style={{textAlign: 'justify'}}>
                            By checking this box, I consent to receive marketing and promotional text messages from Yuccan Technologies at the phone number provided. Messages may include special offers, discounts, and new product updates among others. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                          </span>
                        </label>
                      </div>
                    </>
                  )}

                  {submitMessage && (
                    <div className={`alert ${submitStatus === 'success' ? 'alert-success' : 'alert-danger'}`}>
                      {submitMessage}
                    </div>
                  )}

                  <div className="privacy-notice" style={{margin: '15px 0', fontSize: '14px', color: '#fff'}}>
                    <p style={{fontSize: '16px', margin: 0}}>
                      By submitting this form, you agree to our{" "}
                      <a href="/legal/terms-of-use.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                        Terms of Use
                      </a>
                      {" "}and{" "}
                      <a href="/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </div>

                  <div className="text-center">
                    <button
                      type="submit"
                      className="btn btn-custom btn-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending Request...' : 'Book Your Demo'}
                    </button>
                  </div>
                </form>
            </div>
          </div>
        </div>
      </section>


    </div>
    
    {/* Universal Footer */}
    <Footer />
    </div>
  );
};

export default TelematicsPage;